import { Button, Card, CardContent, CardHeader, CardTitle, toast } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import {
  BanknoteIcon,
  DownloadIcon,
  PercentIcon,
  PrinterIcon,
  ReceiptIcon,
  TagIcon,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import type { FilterConditionDTO } from "@/shared/contracts/base";
import { findCondition } from "@/shared/contracts/query-helpers";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { useReceiptsQuery, useReceiptsSummaryQuery } from "../api/queries";
import {
  exportReceiptsCsv,
  fetchAllReceiptsForExport,
} from "../lib/receipts-export";
import { MergeRoomBookingReceiptPreviewDialog } from "../ui/MergeRoomBookingReceiptPreviewDialog";
import { ReceiptsFilter } from "../ui/ReceiptsFilter";
import { ReceiptsTable } from "../ui/ReceiptsTable";

export function ReceiptsPage() {
  const nav = useNavigate({ from: "/app/billing/receipts" });
  const search = useSearch({
    from: "/app/billing/receipts",
  }) as Record<string, unknown>;

  const sortArray = useMemo((): Array<{
    field: string;
    dir: "asc" | "desc";
  }> => {
    const s = search.sort;
    if (Array.isArray(s) && s[0]) {
      const first = s[0] as { field?: string; dir?: string };
      return [
        {
          field: first.field ?? "issuedAt",
          dir: first.dir === "asc" ? "asc" : "desc",
        },
      ];
    }
    if (typeof s === "string" && s) {
      const field = s.startsWith("-") ? s.slice(1) : s;
      const dir: "asc" | "desc" = s.startsWith("-") ? "desc" : "asc";
      return [{ field, dir }];
    }
    return [{ field: "issuedAt", dir: "desc" }];
  }, [search.sort]);

  const query = useMemo(
    () => ({
      limit: Number(search.limit ?? 20),
      offset: Number(search.offset ?? 0),
      sort: sortArray,
      filters: search.filters as
        | Array<{ field: string; op: string; value: unknown }>
        | undefined,
    }),
    [search.limit, search.offset, sortArray, search.filters],
  );

  const summaryQuery = useMemo(
    () => ({ filters: query.filters }),
    [query.filters],
  );

  const { data, isLoading } = useReceiptsQuery(query);
  const { data: summary, isLoading: summaryLoading } =
    useReceiptsSummaryQuery(summaryQuery);

  const items = data?.data ?? [];
  const total = data?.meta?.total ?? 0;
  const filteredTotal = summary?.totalCount ?? total;

  const filters = (query.filters as FilterConditionDTO[] | undefined) ?? [];
  const billTypeFilter =
    findCondition(filters, "billType") ?? findCondition(filters, "itemType");
  const isRoomBookingMergeMode =
    typeof billTypeFilter?.value === "string" &&
    billTypeFilter.value === "room_booking";

  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeReceiptIds, setMergeReceiptIds] = useState<string[]>([]);
  const [mergeLoading, setMergeLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (!isRoomBookingMergeMode) {
      setMergeOpen(false);
      setMergeReceiptIds([]);
    }
  }, [isRoomBookingMergeMode]);

  const updateSearch = (updates: Record<string, unknown>) => {
    nav({ search: { ...search, ...updates } });
  };

  const handleExportCsv = async () => {
    setExporting(true);
    try {
      const receipts = await fetchAllReceiptsForExport({
        sort: query.sort,
        filters: query.filters,
      });
      if (receipts.length === 0) {
        toast.error("ບໍ່ມີຂໍ້ມູນຕາມເງື່ອນໄຂທີ່ເລືອກ");
        return;
      }
      exportReceiptsCsv(receipts);
      toast.success(`ສົ່ງອອກ CSV ${receipts.length} ລາຍການ`);
    } catch {
      toast.error("ສົ່ງອອກ CSV ລົ້ມເຫຼວ");
    } finally {
      setExporting(false);
    }
  };

  const handleMergePrint = async () => {
    if (!isRoomBookingMergeMode) return;
    setMergeLoading(true);
    try {
      const receipts = await fetchAllReceiptsForExport({
        sort: query.sort,
        filters: query.filters,
      });
      if (receipts.length < 2) {
        toast.error("ຕ້ອງມີໃບຈອງຫ້ອງ ຢ່າງໜ້ອຍ 2 ໃບຕາມ filter");
        return;
      }
      setMergeReceiptIds(receipts.map((r) => r.id));
      setMergeOpen(true);
    } catch {
      toast.error("ໂຫຼດໃບຕາມ filter ບໍ່ສຳເລັດ");
    } finally {
      setMergeLoading(false);
    }
  };

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ໃບເກັບເງິນ" },
          ]}
        />
      </Header>

      <Main>
        <div className="mb-6">
          <h1 className="font-bold text-2xl">ໃບເກັບເງິນ</h1>
          <p className="mt-1 text-muted-foreground text-sm">
            ລາຍການໃບເກັບເງິນທີ່ອອກແລ້ວ
          </p>
        </div>

        <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="font-normal text-muted-foreground text-sm">
                  ຈຳນວນໃບເກັບເງິນທັງໝົດ
                </CardTitle>
                <ReceiptIcon className="size-4 text-muted-foreground" />
              </div>
            </CardHeader>
            <CardContent>
              <p className="font-semibold text-lg">
                {summaryLoading ? "..." : (summary?.totalCount ?? 0)}
              </p>
              <p className="mt-1 text-muted-foreground text-xs">ໃບ</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="font-normal text-muted-foreground text-sm">
                  ຈຳນວນໃບທີ່ມີສ່ວນຫຼຸດ
                </CardTitle>
                <TagIcon className="size-4 text-muted-foreground" />
              </div>
            </CardHeader>
            <CardContent>
              <p className="font-semibold text-lg">
                {summaryLoading ? "..." : (summary?.discountCount ?? 0)}
              </p>
              <p className="mt-1 text-muted-foreground text-xs">ໃບ</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="font-normal text-muted-foreground text-sm">
                  ລາຍຮັບທັງໝົດ
                </CardTitle>
                <BanknoteIcon className="size-4 text-muted-foreground" />
              </div>
            </CardHeader>
            <CardContent>
              <p className="font-semibold text-lg">
                {summaryLoading
                  ? "..."
                  : (summary?.totalAmount ?? 0).toLocaleString("lo-LA")}
              </p>
              <p className="mt-1 text-muted-foreground text-xs">ກີບ</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="font-normal text-muted-foreground text-sm">
                  ສ່ວນຫຼຸດທັງໝົດ
                </CardTitle>
                <PercentIcon className="size-4 text-muted-foreground" />
              </div>
            </CardHeader>
            <CardContent>
              <p className="font-semibold text-lg">
                {summaryLoading
                  ? "..."
                  : (summary?.totalDiscountAmount ?? 0).toLocaleString("lo-LA")}
              </p>
              <p className="mt-1 text-muted-foreground text-xs">ກີບ</p>
            </CardContent>
          </Card>
        </div>

        <div className="flex flex-col rounded-xl border bg-card pt-2">
          <ReceiptsFilter />

          <div className="flex flex-wrap items-center justify-between gap-2 border-b px-2 pb-2">
            <p className="text-muted-foreground text-sm">
              {isRoomBookingMergeMode
                ? `ພິມລວມ / Export ຕາມ filter (${filteredTotal} ໃບ)`
                : `Export ຕາມ filter (${filteredTotal} ໃບ)`}
            </p>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={exporting || filteredTotal === 0}
                onClick={() => void handleExportCsv()}
              >
                <DownloadIcon className="mr-2 size-4" />
                {exporting ? "ກຳລັງສົ່ງອອກ..." : "ສົ່ງອອກ CSV"}
              </Button>
              {isRoomBookingMergeMode ? (
                <Button
                  type="button"
                  size="sm"
                  disabled={mergeLoading || filteredTotal < 2}
                  onClick={() => void handleMergePrint()}
                >
                  <PrinterIcon className="mr-2 size-4" />
                  {mergeLoading ? "ກຳລັງໂຫຼດ..." : "ພິມລວມ"}
                </Button>
              ) : null}
            </div>
          </div>

          <ReceiptsTable
            data={items}
            isLoading={isLoading}
            offset={Number(search.offset ?? 0)}
            limit={Number(search.limit ?? 20)}
            totalCount={total}
            sortBy={query.sort?.[0]?.field ?? "issuedAt"}
            sortOrder={(query.sort?.[0]?.dir ?? "desc") as "asc" | "desc"}
            onPaginationChange={(offset, limit) =>
              updateSearch({ offset, limit })
            }
            onSortingChange={(id, desc) =>
              updateSearch({
                sort: [{ field: id, dir: desc ? "desc" : "asc" }],
                offset: 0,
              })
            }
            onView={(id) => {
              nav({
                to: "/app/billing/receipts/$id",
                params: { id },
              });
            }}
          />
        </div>

        <MergeRoomBookingReceiptPreviewDialog
          open={mergeOpen}
          onOpenChange={setMergeOpen}
          receiptIds={mergeReceiptIds}
        />
      </Main>
    </>
  );
}
